import {Injectable} from '@angular/core';
import {CourseOption, CourseRepository} from '@app/@core/repositories/course.repository';
import {Observable} from 'rxjs';
import {createState, select, Store, withProps} from '@ngneat/elf';
import {tap} from 'rxjs/operators';
import orderBy from 'lodash-es/orderBy';
import {SelectItemGroup} from 'primeng/api';
import {groupBy} from 'lodash-es';
import {localStorageStrategy, persistState} from '@ngneat/elf-persist-state';
import {environment} from '@env/environment';
import {
    deleteAllEntities, deleteEntities,
    getEntities,
    selectAll,
    setEntities,
    upsertEntities,
    withEntities
} from '@ngneat/elf-entities';
import {CourseModel} from '@app/@core/domain/course.model';
import Course = CourseModel.Course;

export interface CourseState {
    options?: CourseOption[];
}

const {state, config} = createState(
    withEntities<Course>(),
    withProps<CourseState>({
        options: []
    })
);

const store = new Store({name: 'courseStore', state, config});

const getState = () => store.getValue();

const updateState = (newState: Partial<CourseState>) => {
    store.update(s => ({...s, ...newState}));
};


@Injectable({
    providedIn: 'root'
})
export class CourseService {
    groupedOptions$ = store.pipe(select((({options}) => {
        const groupByStatus = groupBy(options, 'status');
        const sig: SelectItemGroup[] = [
            {label: 'OPEN', items: orderBy(groupByStatus.OPEN, 'name')},
            {label: 'HIDE', items: orderBy(groupByStatus.HIDE, 'name')},
            {label: 'CLOSE', items: orderBy(groupByStatus.CLOSE, 'name')},
        ];
        return sig;
    })));

    groupedOptionsNotRequire$ = store.pipe(select((({options}) => {
        const groupByStatus = groupBy(options, 'status');
        const sig: SelectItemGroup[] = [
            {
                label: 'OPEN',
                items: [{label: '- Not Select -', value: undefined}, ...orderBy(groupByStatus.OPEN, 'name')]
            },
            {label: 'HIDE', items: orderBy(groupByStatus.HIDE, 'name')},
            {label: 'CLOSE', items: orderBy(groupByStatus.CLOSE, 'name')},
        ];
        return sig;
    })));

    courses$ = store.pipe(selectAll());

    constructor(private repository: CourseRepository) {
        if (environment.cache?.course) {
            persistState(store, {
                key: 'course',
                storage: localStorageStrategy,
            });
        }
    }

    getName(courseId: string) {
        const course = getState().options.find(c => c.value === courseId);
        return course
            ? course.status === 'OPEN' ? course.label : `${course.label}[${course.status}]`
            : courseId;
    }

    hasLoaded() {
        return getState().options.length > 0;
    }

    fetchAllName(): Observable<CourseOption[]> {
        return this.repository.fetchNames().pipe(
            tap(options => updateState({options}))
        );
    }

    // Entities
    getAll(force = false) {
        if (force || store.query(getEntities()).length === 0) {
            this.repository.getAll().subscribe(courses => {
                store.update(deleteAllEntities());
                store.update(setEntities(courses));
            });
        }
    }

    updateCourse(course: Course) {
        store.update(upsertEntities(course));
    }

    delete(id: string) {
        store.update(deleteEntities(id));
    }
}
